import { makeAutoObservable, runInAction } from 'mobx'
import { logger } from 'shared/lib'
import { type IResponseResentMessage, MessagesApi } from 'entities/Message'

type IRecentSearchesStoreConfig = {
  onSearch: (value: string, isRecent: boolean) => void
}

const MAX_COUNT = 15

export class RecentSearchesStore {
  private _loading = false
  private _resentSearchesMap: Map<number, IResponseResentMessage> = new Map()
  private _config: IRecentSearchesStoreConfig | null = null

  constructor() {
    makeAutoObservable(this)
  }

  dispose = () => {
    this._loading = false
    this._resentSearchesMap.clear()
    this._config = null
  }

  setConfig = (config: IRecentSearchesStoreConfig) => {
    this._config = config
  }

  loadItems = async () => {
    try {
      runInAction(() => {
        this._loading = true
      })

      const { data } = await MessagesApi.getRecentSearches(20)
      this._resentSearchesMap = new Map(data.map((item) => [item.id, item]))
    } catch (e) {
      logger.error(e)
    } finally {
      runInAction(() => {
        this._loading = false
      })
    }
  }

  createItem = async (value: string) => {
    try {
      if (value.length > 2) {
        const { data } = await MessagesApi.createRecentSearches(value)
        runInAction(() => {
          if (this._resentSearchesMap.size > MAX_COUNT && this.lastItem) {
            this.removeItem(this.lastItem.id)
          }

          this._resentSearchesMap = new Map([[data.id, data], ...this._resentSearchesMap])
        })
      }
    } catch (e) {
      logger.error(e)
    }
  }

  removeItem = async (id: number) => {
    try {
      this._resentSearchesMap.delete(id)
      await MessagesApi.deleteRecentSearches(id)
    } catch (e) {
      console.error(e)
    }
  }

  setSearch = (value: string, isRecent: boolean) => {
    this._config?.onSearch(value, isRecent)
  }

  get lastItem() {
    const items = Array.from(this._resentSearchesMap.values())

    return items[items.length - 1]
  }

  get items() {
    const items = Array.from(this._resentSearchesMap.values())

    if (items.length > MAX_COUNT) {
      return items.filter((item, index) => index + 1 <= MAX_COUNT)
    }

    return items
  }

  get count() {
    return this.items.length
  }

  get loading() {
    return this._loading
  }
}
